import { useEffect, useRef, useState } from 'react';
import { ensureFreshAccessTokenForStream } from '@shared/api/ensureFreshAccessTokenForStream';
import {
  openSalesOrderEventStream,
  type ConnectionState,
} from '../lib/salesOrderEventStream';
import type { SalesOrderEvent } from '../types/salesOrder.types';

// FO-SOI-3: EventTarget-based local bus that fans the single SSE connection to
// whichever components subscribe. Module-scoped so any consumer that opens the
// list page shares one bus instance. Mirrors features/quotations/hooks/
// useQuotationEventStream.ts.

const eventTarget = new EventTarget();
const EVENT_NAME = 'csc:sales-order-event';
const RECONNECT_NAME = 'csc:sales-order-reconnect';

export function onSalesOrderEvent(handler: (evt: SalesOrderEvent) => void): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<SalesOrderEvent>).detail);
  eventTarget.addEventListener(EVENT_NAME, listener);
  return () => eventTarget.removeEventListener(EVENT_NAME, listener);
}

export function onSalesOrderStreamReconnect(handler: () => void): () => void {
  const listener = () => handler();
  eventTarget.addEventListener(RECONNECT_NAME, listener);
  return () => eventTarget.removeEventListener(RECONNECT_NAME, listener);
}

interface UseSalesOrderEventStreamResult {
  connectionState: ConnectionState;
}

export function useSalesOrderEventStream(): UseSalesOrderEventStreamResult {
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  const handleRef = useRef<{ close: () => void } | null>(null);

  useEffect(() => {
    // E7 item 4 — visibility gating; see useQuotationEventStream for the full rationale (connection
    // slots, expiring tokens, and why missing events while hidden is safe given the catch-up on reopen).
    const open = () => {
      if (handleRef.current) return;
      handleRef.current = openSalesOrderEventStream({
        onEvent: (evt) => {
          eventTarget.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: evt }));
        },
        onConnectionStateChange: setConnectionState,
        onReconnect: () => {
          eventTarget.dispatchEvent(new CustomEvent(RECONNECT_NAME));
        },
      });
    };

    const close = () => {
      handleRef.current?.close();
      handleRef.current = null;
    };

    let disposed = false;

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') {
        close();
        return;
      }
      // Refresh before reopening — see useQuotationEventStream for why the reopen path needs this
      // even though scheduleReconnect() already has it.
      void ensureFreshAccessTokenForStream().then(() => {
        if (disposed || document.visibilityState !== 'visible') return;
        open();
        // A reopen is a gap like any drop — make consumers refetch what they missed.
        eventTarget.dispatchEvent(new CustomEvent(RECONNECT_NAME));
      });
    };

    close(); // Strict mode double-invoke: never leave a previous handle open.
    // First open needs no refresh: AuthInitializer already refreshes a stale token at mount.
    if (document.visibilityState === 'visible') open();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      close();
    };
  }, []);

  return { connectionState };
}
